/**
 * Excerpt strips — verify-at-source P1 (Ale 2026-08-03, approved mock v3).
 *
 * The focused proposal card's source passages, rendered as document-pixel
 * strips right where the officer is deciding. Each strip is the cited page
 * cropped around the extractor's verbatim snippet; clicking it opens the
 * full document at that page (drill-down). Honesty states are spelled out —
 * a passage we can't locate or a scanned page never pretends to be a match.
 */
import { useEffect, useState } from 'react'
import { FileText, AlertTriangle } from 'lucide-react'
import { renderExcerpt, type ExcerptResult } from '../../lib/excerptEngine'
import { useAuthSession } from '../../lib/authSession'
import DocumentLink from '../../v2/documents/DocumentLink'

export interface ExcerptSource {
  documentId: string
  filename: string
  /** 1-indexed cited page; absent = no location captured for this claim. */
  page?: number
  /** Verbatim snippet from the extractor (source_coordinate.text). */
  text?: string
  /** §-tag shown on the strip ("§A"). */
  label?: string
}

const STATUS_NOTE: Record<ExcerptResult['status'], string | null> = {
  located: null,
  page_only: 'passage not locatable — top of the page shown',
  no_text: 'scanned page, no text layer — top of the page shown, verify in the document',
  error: 'could not render the source page',
}

function Strip({
  src,
  onOpenDoc,
}: {
  src: ExcerptSource
  onOpenDoc: (documentId: string, page?: number) => void
}) {
  const session = useAuthSession()
  const [loaded, setLoaded] = useState<{ session: string | null; result: ExcerptResult } | null>(
    null
  )
  const result = loaded?.session === session ? loaded.result : null

  useEffect(() => {
    if (!src.page) return
    let live = true
    renderExcerpt(src.documentId, src.page, src.text).then((r) => {
      if (live) setLoaded({ session, result: r })
    })
    return () => {
      live = false
    }
  }, [src.documentId, src.page, src.text, session])

  const note = src.page
    ? result && STATUS_NOTE[result.status]
    : 'no source location captured for this claim — open the document to verify'

  return (
    <div data-testid="excerpt-strip">
      <div className="flex items-center gap-1.5 mb-1">
        {src.label && (
          <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1">
            {src.label}
          </span>
        )}
        <FileText size={10} className="text-slate-400 shrink-0" />
        <span className="text-[10px] text-slate-500 truncate">
          <DocumentLink document={{ document_id: src.documentId, page: src.page }} />
        </span>
        {src.page && <span className="text-[10px] text-slate-400 font-data">p. {src.page}</span>}
        {note && (
          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
            <AlertTriangle size={10} /> {note}
          </span>
        )}
      </div>
      {src.page && !result && (
        <div className="h-14 rounded border border-slate-200 bg-slate-50 animate-pulse" />
      )}
      {result?.imageUrl && (
        // Every state is a full-width strip of readable document pixels —
        // located crops around the match, fallbacks crop the page top
        // (title, parties, headers) instead of a shrunken whole page.
        <button
          type="button"
          onClick={() => onOpenDoc(src.documentId, src.page)}
          title="Open the full document at this page"
          className="block w-full rounded border border-slate-200 hover:border-nolos-primary overflow-hidden cursor-zoom-in"
        >
          <img
            src={result.imageUrl}
            alt={`Original document passage on page ${src.page}`}
            className="w-full h-auto block"
          />
        </button>
      )}
    </div>
  )
}

/** The focused card's source block: one strip per cited passage. */
export default function ExcerptStrips({
  sources,
  onOpenDoc,
}: {
  sources: ExcerptSource[]
  onOpenDoc: (documentId: string, page?: number) => void
}) {
  if (sources.length === 0) return null
  return (
    <div className="space-y-2" data-testid="excerpt-strips">
      <div className="text-[9px] font-medium uppercase tracking-wide text-slate-400">
        Source — verify before approving
      </div>
      {sources.map((s, i) => (
        <Strip key={`${s.documentId}:${s.page}:${i}`} src={s} onOpenDoc={onOpenDoc} />
      ))}
    </div>
  )
}
